import React, { useEffect, useRef, useState } from 'react';
import { db, functions } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { logAuditEvent, logError } from '../services/Logger';
import { IS_DEMO } from '../constants/featureFlags';
import { usePageLoad } from '../hooks/usePageLoad';
import LoadErrorNotice from './LoadErrorNotice';
import {
  MESSAGE_MAX_LENGTH, asText, feedbackTypeColors, feedbackTypeLabel, fetchMyFeedback, formatFeedbackDate, hasReply,
  isReplyUnread, markReplySeen,
} from '../utils/feedbackReplies';

/**
 * Tell the administrator a message arrived (v3.90.0). Fire-and-forget: the
 * message is already saved, so nothing here may fail the send or delay the
 * thank-you. The callable queues an email that carries the sender, the type
 * and the page — never the message text. The demo project has no Cloud
 * Functions (and denies the create), so it is not called there.
 */
function notifyFeedbackSubmitted(feedbackId) {
  if (IS_DEMO || !feedbackId) return;
  try {
    Promise.resolve(httpsCallable(functions, 'notifyFeedbackSubmitted')({ feedbackId }))
      .catch((err) => console.warn('Feedback notification was not queued:', err?.code || err?.message || err));
  } catch (err) {
    console.warn('Feedback notification was not queued:', err?.message || err);
  }
}

/**
 * "Your messages" (v3.90.0): everything this user has sent through Send
 * Feedback, newest first, with the administrator's reply under each one.
 * Loaded when the view opens — one bounded query (feedbackReplies.js). A
 * reply shown here counts as read: it is acknowledged and the home-page card
 * for it goes away (`onRepliesSeen`). The list keeps the "New reply" label
 * for this viewing, so a reply acknowledged by opening the list can still be
 * found in it. An administrator viewing as the user (`readOnly`) reads
 * without acknowledging.
 */
function MyFeedbackList({ user, readOnly, onRepliesSeen }) {
  const [items, setItems] = useState([]);
  const seenRef = useRef(onRepliesSeen);
  seenRef.current = onRepliesSeen;
  const acknowledgedRef = useRef(new Set());

  const { loading, error, retry } = usePageLoad(async () => {
    setItems(await fetchMyFeedback(user.uid));
  }, [user.uid]);

  // Acknowledge what is on screen — after the list is shown, never in its
  // way. Best effort: a failed write just leaves the card on the home page.
  useEffect(() => {
    if (readOnly) return;
    const unreadIds = items.filter(isReplyUnread).map((m) => m.id).filter((id) => !acknowledgedRef.current.has(id));
    if (unreadIds.length === 0) return;
    unreadIds.forEach((id) => acknowledgedRef.current.add(id));
    Promise.allSettled(unreadIds.map((id) => markReplySeen(id))).then((results) => {
      const acknowledged = unreadIds.filter((_, i) => results[i].status === 'fulfilled');
      if (acknowledged.length && seenRef.current) seenRef.current(acknowledged);
    });
  }, [items, readOnly]);

  if (loading) return <p className="my-feedback-status">Loading your messages…</p>;
  if (error) return <LoadErrorNotice error={error} onRetry={retry} compact />;
  if (items.length === 0) {
    return <p className="my-feedback-status">You have not sent any messages yet. When you do, they are listed here with our reply.</p>;
  }

  return (
    <ul className="my-feedback-list">
      {items.map((item) => {
        const sent = formatFeedbackDate(item.timestamp);
        const answered = formatFeedbackDate(item.replyAt);
        return (
          <li key={item.id} className="my-feedback-item">
            <div className="my-feedback-meta">
              <span className="my-feedback-type" style={feedbackTypeColors(item.type)}>{feedbackTypeLabel(item.type)}</span>
              {sent && <span>{sent}</span>}
              {isReplyUnread(item) && <span className="my-feedback-new">New reply</span>}
            </div>
            <p className="my-feedback-message">{asText(item.message)}</p>
            {hasReply(item) ? (
              <div className="my-feedback-reply">
                <div className="my-feedback-reply-head">
                  Reply from {asText(item.replyByName, 'Literacy Assessments')}{answered ? ` · ${answered}` : ''}
                </div>
                <p className="my-feedback-reply-text">{asText(item.replyText)}</p>
              </div>
            ) : (
              <p className="my-feedback-noreply">No reply yet.</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function FeedbackModal({ isOpen, onClose, user, initialView = 'new', isImpersonating = false, onRepliesSeen }) {
  const [type, setType] = useState('Question');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState(initialView);

  // Each opening starts on the view it was opened for (the sidebar entry opens
  // the form; the home-page reply card opens Your messages). Decided during
  // render, not in an effect: an effect would let the list mount — and read —
  // for one render on an opening that is meant to show the form.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setView(initialView);
  }

  if (!isOpen || isOpen !== wasOpen) return null;

  // The demo project denies feedback writes, so there is never a message to list.
  const showMessagesTab = !IS_DEMO;
  const activeView = showMessagesTab ? view : 'new';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    try {
      const ref = await addDoc(collection(db, 'app_feedback'), {
        type,
        message: message.trim(),
        userEmail: user.email,
        userName: user.displayName || user.email.split('@')[0],
        userId: user.uid,
        status: 'new',
        timestamp: serverTimestamp(),
        url: window.location.pathname
      });

      notifyFeedbackSubmitted(ref?.id);
      await logAuditEvent(user, 'FEEDBACK_SUBMITTED', 'feedback_form', { type });

      alert('Thank you for your feedback! If we reply, you will see the answer here in the app.');
      setMessage('');
      setType('Question');
      onClose();
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      await logError(user, 'FEEDBACK_SUBMIT_ERROR', err.message, err.stack);
      alert("Failed to submit feedback. Please try again or email the administrator if the issue persists.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabStyle = (active) => ({
    padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.95em',
    border: active ? '1px solid #3b82f6' : '1px solid #cbd5e1',
    background: active ? '#eff6ff' : 'white',
    color: active ? '#1d4ed8' : '#475569',
  });

  return (
    <div className="modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000
    }}>
      <div className="modal-content" style={{
        background: 'white', padding: '30px', borderRadius: '12px',
        width: '90%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
      }}>
        <h2 style={{ marginTop: 0, color: '#334155' }}>Send Feedback</h2>

        {showMessagesTab && (
          <div role="tablist" aria-label="Feedback" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button
              type="button" role="tab" id="feedback-tab-new" aria-controls="feedback-panel"
              aria-selected={activeView === 'new'}
              onClick={() => setView('new')} style={tabStyle(activeView === 'new')}
            >
              New message
            </button>
            <button
              type="button" role="tab" id="feedback-tab-mine" aria-controls="feedback-panel"
              aria-selected={activeView === 'mine'}
              onClick={() => setView('mine')} style={tabStyle(activeView === 'mine')}
            >
              Your messages
            </button>
          </div>
        )}

        <div
          id="feedback-panel"
          role={showMessagesTab ? 'tabpanel' : undefined}
          aria-labelledby={showMessagesTab ? `feedback-tab-${activeView}` : undefined}
        >
        {activeView === 'mine' ? (
          <div>
            <MyFeedbackList user={user} readOnly={isImpersonating} onRepliesSeen={onRepliesSeen} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{ padding: '10px 15px', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', color: '#64748b', fontSize: '0.95em' }}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
        <>
        <p style={{ color: '#64748b', fontSize: '0.95em', marginBottom: '20px' }}>
          Have a question, found a bug, or want to suggest a feature? Let us know!
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#475569' }}>Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '1em' }}
            >
              <option value="Question">Question</option>
              <option value="Bug">Report a Bug</option>
              <option value="Feature Request">Feature Request</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#475569' }}>Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please describe your issue or suggestion in detail..."
              rows={5}
              required
              maxLength={MESSAGE_MAX_LENGTH}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '1em', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{ padding: '10px 15px', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', color: '#64748b' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !message.trim()}
              style={{ padding: '10px 20px', background: '#3b82f6', border: 'none', borderRadius: '6px', color: 'white', fontWeight: 'bold', cursor: isSubmitting || !message.trim() ? 'not-allowed' : 'pointer', opacity: isSubmitting || !message.trim() ? 0.7 : 1 }}
            >
              {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </button>
          </div>
        </form>
        </>
        )}
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;
